import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Icon, IconType } from '../Props/Icon/Icon';

export type DropdownOption = {
  value: string;
  label: string | ReactNode;
};

type BaseDropdownProps = {
  options: DropdownOption[];
  placeholder?: string | ReactNode;
  allowScroll?: boolean;
  maxHeight?: string;
  className?: string;
  disabled?: boolean;
};

type SingleSelectProps = BaseDropdownProps & {
  multiSelect?: false;
  clearable?: boolean;
  onChange: (selected: DropdownOption | undefined) => void;
  value?: DropdownOption;
};

type MultiSelectProps = BaseDropdownProps & {
  multiSelect: true;
  clearable?: never; // disallow clearable in multi-select mode
  onChange: (selected: DropdownOption[] | undefined) => void;
  value?: DropdownOption[];
};

type DropdownProps = SingleSelectProps | MultiSelectProps;

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  placeholder = 'Select option',
  multiSelect = false,
  clearable = true,
  allowScroll = true,
  maxHeight = '200px',
  onChange,
  value,
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<DropdownOption[]>([]);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number }>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        setSelectedOptions(value);
      } else {
        setSelectedOptions([value]);
      }
    } else {
      setSelectedOptions([]);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideTrigger = dropdownRef.current?.contains(target);
      // The list is rendered in a portal (outside dropdownRef), so check it separately.
      const clickedInsideList = listRef.current?.contains(target);
      if (!clickedInsideTrigger && !clickedInsideList) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Position the portaled list under the trigger, and keep it aligned while open.
  const updateMenuPosition = useCallback(() => {
    const rect = headerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    updateMenuPosition();
    // Capture phase so scrolling within any ancestor (e.g. a modal) repositions the list.
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: DropdownOption) => {
    let newSelectedOptions: DropdownOption[];

    if (multiSelect) {
      const isSelected = selectedOptions.some((item) => item.value === option.value);
      newSelectedOptions = isSelected
        ? selectedOptions.filter((item) => item.value !== option.value)
        : [...selectedOptions, option];
    } else {
      newSelectedOptions = [option];
      setIsOpen(false);
    }

    setSelectedOptions(newSelectedOptions);

    if (newSelectedOptions.length === 0) {
      if (multiSelect) {
        (onChange as MultiSelectProps['onChange'])([]);
      } else {
        (onChange as SingleSelectProps['onChange'])(undefined);
      }
    } else {
      if (multiSelect) {
        (onChange as MultiSelectProps['onChange'])(newSelectedOptions);
      } else {
        (onChange as SingleSelectProps['onChange'])(newSelectedOptions[0]);
      }
    }
  };

  const removeOption = (option: DropdownOption, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelectedOptions = selectedOptions.filter((item) => item.value !== option.value);
    setSelectedOptions(newSelectedOptions);

    if (newSelectedOptions.length === 0) {
      if (multiSelect) {
        (onChange as MultiSelectProps['onChange'])([]);
      } else {
        (onChange as SingleSelectProps['onChange'])(undefined);
      }
    } else {
      if (multiSelect) {
        (onChange as MultiSelectProps['onChange'])(newSelectedOptions);
      } else {
        (onChange as SingleSelectProps['onChange'])(newSelectedOptions[0]);
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOptions([]);
    (onChange as SingleSelectProps['onChange'])(undefined);
  };

  const isOptionSelected = (option: DropdownOption) => {
    return selectedOptions.some((item) => item.value === option.value);
  };

  return (
    <DropdownContainer ref={dropdownRef} className={className}>
      <DropdownHeader ref={headerRef} className="dropdown-header" onClick={toggleDropdown} disabled={disabled}>
        <SelectedItemsContainer>
          {selectedOptions.length === 0 ? (
            <Placeholder>{placeholder}</Placeholder>
          ) : multiSelect ? (
            selectedOptions.map((option) => (
              <SelectedPill key={option.value}>
                {option.label}
                <RemoveButton
                  onClick={(e) => removeOption(option, e)}
                  aria-label={`Remove ${typeof option.label === 'string' ? option.label : 'selected option'}`}
                >
                  <Icon width={16} height={16} icon={IconType.TIMES} />
                </RemoveButton>
              </SelectedPill>
            ))
          ) : (
            selectedOptions[0]?.label
          )}
        </SelectedItemsContainer>
        {/* Render clear button only for single select when clearable is true and a value is selected */}
        {!multiSelect && clearable && selectedOptions.length > 0 && (
          <RemoveButton onClick={handleClear} aria-label="Clear selection">
            <Icon width={16} height={16} icon={IconType.TIMES} />
          </RemoveButton>
        )}
        <StyledArrow isOpen={isOpen} icon={IconType.CHEVRON_DOWN} width={16} height={16} />
      </DropdownHeader>

      {isOpen &&
        menuPosition &&
        ReactDOM.createPortal(
          <DropdownList
            ref={listRef}
            className="dropdown-list"
            $allowScroll={allowScroll}
            $maxHeight={maxHeight}
            style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}
          >
            {options.map((option) => (
              <DropdownItem
                key={option.value}
                $isSelected={isOptionSelected(option)}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </DropdownItem>
            ))}
          </DropdownList>,
          document.body,
        )}
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div<{ disabled?: boolean }>`
  padding: 12px 12px;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.prototype.card};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const DropdownList = styled.ul<{ $allowScroll: boolean; $maxHeight: string }>`
  position: fixed;
  margin-top: 5px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.prototype.card};
  z-index: 1000;
  list-style: none;
  box-shadow: ${({ theme }) => theme.shadow.light};
  max-height: ${({ $allowScroll, $maxHeight }) => ($allowScroll ? $maxHeight : 'none')};
  overflow-y: ${({ $allowScroll }) => ($allowScroll ? 'auto' : 'visible')};
`;

const DropdownItem = styled.li<{ $isSelected: boolean }>`
  padding: 10px 15px;
  cursor: pointer;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.prototype.secondary : theme.colors.prototype.card};
  transition: background-color 0.2s ease-in-out;
  text-align: start;

  &:hover {
    background-color: ${({ theme }) => theme.colors.prototype.secondary};
  }
`;

const StyledArrow = styled(Icon)<{ isOpen: boolean }>`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s ease;
`;

const SelectedItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  flex: 1;
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const SelectedPill = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${({ theme }) => theme.colors.prototype.secondary};
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 16px;
  padding: 4px 8px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
`;

const Placeholder = styled.span`
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;
